import {attr, controller, target, targets} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller('actions-calculator')
export class ActionsCalculatorElement extends HTMLElement {
  // GitHub-hosted ---------------------------------------

  // OS Types
  @targets declare osTypes: HTMLInputElement[]
  get selectedOsType(): HTMLInputElement | undefined {
    return this.osTypes.find(opt => opt.checked)
  }
  get osTypeLabel() {
    return (this.selectedOsType && this.selectedOsType.getAttribute('data-label')) || ''
  }
  get osType() {
    return (this.selectedOsType && this.selectedOsType.value) || ''
  }

  // Runner Types
  @targets declare runnerTypes: HTMLInputElement[]
  get selectedRunnerType(): HTMLInputElement | undefined {
    return this.runnerTypes.find(opt => opt.checked)
  }
  get runnerTypeLabel() {
    return (this.selectedRunnerType && this.selectedRunnerType.getAttribute('data-label')) || ''
  }
  get runnerType() {
    return (this.selectedRunnerType && this.selectedRunnerType.value) || ''
  }

  // Core Sizes
  @targets declare coreSizes: HTMLInputElement[]
  get selectedCoreSize(): HTMLInputElement | undefined {
    return this.coreSizes.find(opt => opt.checked)
  }
  get coreSizeLabel() {
    return (this.selectedCoreSize && this.selectedCoreSize.getAttribute('data-label')) || ''
  }
  get coreSize() {
    return (this.selectedCoreSize && this.selectedCoreSize.value) || ''
  }
  get runner() {
    return (this.selectedCoreSize && this.selectedCoreSize.getAttribute('data-runner')) || ''
  }

  // Job Duration
  @target declare jobDurationInput: HTMLInputElement
  get jobDuration() {
    return Number(this.jobDurationInput.value) || 0
  }

  // Validation
  get hasValidInputs() {
    if (!(this.jobDuration && this.selectedRunnerType && this.selectedCoreSize)) {
      return false
    }

    if (!this.osType) {
      return false
    }

    const runnerCostKey = this.coreSize
    if (!(runnerCostKey in this.unit_data)) {
      return false
    }

    return true
  }

  @targets declare runners: ActionRunnerElement[]
  @target declare addRunnerButton: HTMLElement
  @target declare osTypesContainer: HTMLElement
  @target declare runnerTypesContainer: HTMLElement
  @target declare coreSizesContainer: HTMLElement
  @target declare runnersContainer: HTMLElement
  @target declare runnersList: HTMLElement
  @target declare runnerTemplate: HTMLTemplateElement
  @target declare costPerMonthDisplay: HTMLElement

  @attr costPerMonth = 0
  @attr active = false
  @attr runnerCount = 0

  declare unit_data: UnitData

  connectedCallback() {
    this.unit_data = getUnitData()
    this.updateCoreSizes()
  }

  osTypeChanged() {
    this.updateRunnerTypes()
    this.updateCoreSizes()
    this.validateInputs()
  }

  runnerTypeChanged() {
    this.updateCoreSizes()
    this.validateInputs()
  }

  coreSizesChanged() {
    this.validateInputs()
  }

  updateRunnerTypes() {
    const gpuOption = this.runnerTypesContainer.querySelector<HTMLElement>('[data-runner-type="action_runners_gpu"]')
    if (!gpuOption) return

    // Hide GPU option for macOS
    if (this.osType === 'action_runners_macos') {
      gpuOption.hidden = true

      if (this.selectedRunnerType?.value === 'action_runners_gpu') {
        // choose previous item, if the selected option is being hidden
        this.selectedRunnerType.parentElement?.previousElementSibling?.querySelector<HTMLInputElement>('input')?.click()

        // reset core size selection
        this.resetCoreSizes()
      }
    } else {
      gpuOption.hidden = false
    }
  }

  updateCoreSizes() {
    this.resetCoreSizes()

    const allCoreSizeOptions = this.coreSizesContainer.querySelectorAll<HTMLElement>('[data-os][data-runner-type]')
    for (const option of allCoreSizeOptions) {
      option.hidden = true
    }

    const matchingOption = this.coreSizesContainer.querySelector<HTMLElement>(
      `[data-os="${this.osType}"][data-runner-type="${this.runnerType}"]`,
    )

    if (matchingOption) {
      matchingOption.hidden = false
    }
  }

  validateInputs() {
    if (this.hasValidInputs) {
      this.addRunnerButton.classList.remove('grayscale')
      this.addRunnerButton.removeAttribute('disabled')
    } else {
      this.addRunnerButton.classList.add('grayscale')
      this.addRunnerButton.setAttribute('disabled', 'disabled')
    }
  }

  addRunnerButtonClicked() {
    if (this.hasValidInputs) {
      const runnerCostKey = this.coreSize
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const runnerCost = this.unit_data[runnerCostKey]!.value
      const cost = this.calculateRunnerCost(runnerCost, this.jobDuration)

      this.appendRunner(this.runner, cost, this.jobDuration)
      this.updateMonthlyCost()
      this.resetForm()
    }
  }

  resetCoreSizes() {
    for (const coreSizeOption of this.coreSizes) {
      coreSizeOption.checked = false
      coreSizeOption.dispatchEvent(new Event('change'))
    }
  }

  resetForm() {
    this.jobDurationInput.value = ''
    this.jobDurationInput.dispatchEvent(new Event('input'))

    this.resetCoreSizes()
  }

  // Runner List ----------------------------------------------------

  appendRunner(runner: string, cost: number, jobDuration: number) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const newRunner = this.runnerTemplate.content.firstElementChild!.cloneNode(true) as ActionRunnerElement
    newRunner.runner = runner
    newRunner.cost = cost
    newRunner.jobDuration = jobDuration

    this.runnersList.prepend(newRunner)
    this.runnersContainer.hidden = false
    this.runnerCount = this.runners.length
  }

  calculateRunnerCost(runnerCost: number, jobDuration: number) {
    return runnerCost * jobDuration
  }

  updateMonthlyCost() {
    this.costPerMonth = this.runners.reduce((accum, runner) => accum + runner.cost, 0)
  }

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    this.resetForm()
    this.runnersList.textContent = ''
    this.runnersContainer.hidden = true
    this.costPerMonth = 0
  }

  getSummaryForCSV() {
    return this.runners.map(runner => runner.getSummaryForCSV())
  }

  static observedAttributes = ['data-cost-per-month', 'data-runner-count', 'data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return
    }

    if (name === 'data-cost-per-month' || name === 'data-active') {
      this.costPerMonthDisplay.textContent = currencyFormatter.format(this.costPerMonth / 100)
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'actionsTotal', amount: this.costPerMonth, active: this.active},
        }),
      )
    }

    if (name === 'data-cost-per-month' || name === 'data-runner-count') {
      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'actions',
            resetEnabled: Boolean(this.runnerCount) || Boolean(this.costPerMonth),
          },
        }),
      )
    }
  }
}

@controller('action-runner')
class ActionRunnerElement extends HTMLElement {
  @attr declare runner: string
  @attr declare jobDuration: number
  @attr declare cost: number
  get formattedCost() {
    return this.cost === 0 ? 'Free' : currencyFormatter.format(this.cost / 100)
  }

  @target declare runnerDisplay: HTMLElement
  @target declare jobDurationDisplay: HTMLElement
  @target declare costDisplay: HTMLElement

  connectedCallback() {
    this.runnerDisplay.textContent = String(this.runner)
    this.jobDurationDisplay.textContent = String(this.jobDuration)
    this.costDisplay.textContent = this.formattedCost

    this.addEventListener('transitionend', () => {
      const actionsCalculator = this.closest('actions-calculator') as ActionsCalculatorElement
      this.remove()
      actionsCalculator.updateMonthlyCost()
      actionsCalculator.runnersContainer.hidden = !actionsCalculator.querySelectorAll('action-runner').length
    })
  }

  removeSelf() {
    // DOM is updated in transitionend listener of connectedCallback
    this.classList.add('action-runner--deleted')
  }

  getSummaryForCSV() {
    // headers: ['Service', 'Configuration Summary', 'Notes', 'Cost Per Month']

    return [
      'Actions',
      `"Runner: ${this.runner} · ${this.jobDuration} min / month"`,
      '',
      `"${currencyFormatter.format(this.cost / 100)}"`,
    ]
  }
}
